<template>
  <div v-if="!loading">
    <form v-if="!requestedSalesOrder" @submit.prevent="submit">
      <legend class="sr-only">
        {{ i18n.t('/QuoteActions/Forms/RequestSalesOrderTitle') }}
      </legend>
      <div>
        <app-fieldset :title="i18n.t('/QuoteActions/Forms/RequestSalesOrderTitle')">
          <form-text-area
            id="emailBody"
            v-model="emailBody"
            name="emailBody"
            title="Email body"
            rows="5"
            :show-label="false"
          />

          <form-checkbox id="sendEmailToCustomer" :title="mailCustomerLabel">
            <input
              id="sendEmailToCustomer"
              v-model="sendEmailToCustomer"
              type="checkbox"
              name="sendEmailToCustomer"
            />
          </form-checkbox>

          <form-checkbox id="sendCopyToEmployee" :title="mailCopyToEmployee">
            <input
              id="sendCopyToEmployee"
              v-model="sendCopyToEmployee"
              type="checkbox"
              name="sendCopyToEmployee"
            />
          </form-checkbox>

          <form-checkbox
            v-if="digitalSigningEnabled && isSigningStatusEmpty && !isSalesOrder"
            id="signDigitally"
            :title="i18n.t('/QuoteActions/Forms/DigitalSigning/SignDigitally')"
          >
            <input
              id="signDigitally"
              v-model="signDigitally"
              type="checkbox"
              name="signDigitally"
            />
          </form-checkbox>

          <form-checkbox
            v-if="isStockCarOrder && !additionalQuote"
            id="reserveInIDS"
            :title="i18n.t('/QuoteActions/Forms/ReserveCarInIdsLabel')"
          >
            <input id="reserveInIDS" v-model="reserveInIDS" type="checkbox" name="reserveInIDS" />
          </form-checkbox>

          <form-checkbox
            v-if="reserveOwnStockCarButtonEnabled"
            id="reserveStockCar"
            :title="i18n.t('/QuoteActions/Forms/ReserveOwnStockCarLabel')"
          >
            <input
              id="reserveStockCar"
              v-model="reserveStockCar"
              type="checkbox"
              name="reserveStockCar"
            />
          </form-checkbox>

          <form-checkbox
            v-if="!isStockCarOrder && !additionalQuote"
            id="orderMultiple"
            :title="i18n.t('/QuoteActions/Forms/MultipleOrdersLabel')"
          >
            <input
              id="orderMultiple"
              v-model="orderMultiple"
              type="checkbox"
              name="orderMultiple"
            />
          </form-checkbox>

          <form-input
            v-if="orderMultiple"
            id="orderAmount"
            v-model="orderAmount"
            :title="i18n.t('/QuoteActions/Forms/MultipleOrdersLabel')"
            :error-message="orderMultipleErrorMessage"
            type="number"
            min="1"
            max="25"
          />
        </app-fieldset>
      </div>

      <app-button
        :disabled="
          loading || (orderMultiple && maxOrderAmountError) || signDigitallyAndMakeMultipleSelected
        "
        data-qa="send-sales-order-form-button"
        type="submit"
        button-type="cta"
        class="submit-button"
      >
        {{ i18n.t('/Common/Send') }}
      </app-button>

      <confirmation-prompt
        id="manual-signing-prompt"
        :show="showManualSigningPrompt"
        :title="manualSigningTitle"
        :info-text="manualSigningText"
        :extra-wide="true"
        :confirm-button-text="i18n.t('/QuoteActions/Forms/ManualSign/ConfirmManualSign')"
        :cancel-button-text="i18n.t('/QuoteActions/Forms/ManualSign/SkipManualSign')"
        :disabled="orderAmountMoreThanOne"
        :cancel-button-show="!expectedDeliveryDateUnknownAndForced"
        :confirm-button-show="!expectedDeliveryDateUnknownAndForced"
        @confirm="manualQuoteSignSubmit"
        @cancel="manualQuoteSignSkip"
        @close="showManualSigningPrompt = false"
      >
      </confirmation-prompt>

      <modal
        v-if="showDigitalSigningModal"
        :title="i18n.t('/QuoteActions/Forms/RequestSalesOrderTitle')"
        @close="toggleDigitalSigningModal(false)"
      >
        <digital-signing-form
          :entity-type="entityType.quote"
          @close="toggleDigitalSigningModal(false)"
          @submit-digital-signing="submitDigitalSigning"
        />
      </modal>
      <digital-login-redirect-form
        :show="showDigitalLoginRedirectModal"
        :digital-redirect-url="digitalRedirectUrl"
        @close="toggleDigitalLoginRedirectModal(false)"
      />

      <!-- Second popup for approval confirmation -->
      <modal
        v-if="showApprovalConfirmModal"
        :title="approvalFormTitle"
        max-width="45%"
        @close="showApprovalConfirmModal = false"
      >
        <form @submit.prevent="submitForApproval">
          <div>
            <p>{{ approvalFormDescription }}</p>

            <form-input
              v-if="orderMultiple"
              id="orderAmountApproval"
              v-model="orderAmount"
              :title="i18n.t('/QuoteActions/Forms/MultipleOrdersLabel')"
              :error-message="orderMultipleErrorMessage"
              type="number"
              min="1"
              max="25"
            />
          </div>

          <div class="modal-buttons">
            <app-button
              :disabled="loading || (orderMultiple && maxOrderAmountError) || isProcessingApproval"
              data-qa="send-for-approval-button"
              type="submit"
              button-type="cta"
            >
              {{ sendForApprovalButtonText }}
            </app-button>
          </div>
        </form>
      </modal>
    </form>
    <div v-else>
      <!-- SalesOrder requested -->
      <div v-if="!hasErrors">
        <div v-if="!additionalQuote">
          <p v-if="salesApprovalEnabled && !isSalesOrder">
            {{ i18n.t('/QuoteActions/Forms/SalesOrder/SendToSalesManagerForApproval') }}
          </p>
          <p v-else-if="!signDigitally && !signManually && !sendEmailOnly">
            {{ i18n.t('/QuoteActions/Forms/SalesOrder/SuccessOrderHeaderTitle') }}
          </p>
          <p v-else>
            {{ i18n.t('/QuoteActions/Forms/SalesOrder/SuccessSkipAutolineHeaderTitle') }}
          </p>
          <div v-if="showLocalSigningUrlButton && !goPrinterEnabled">
            <p>
              {{ i18n.t('/QuoteActions/Forms/DigitalSigning/ScanQrCodeText') }}
            </p>

            <div class="signing-qr-code-wrapper">
              <img class="signing-qr-code" :src="signingQrCodeImageSrc" />
            </div>
          </div>
        </div>
        <p v-else>
          {{ i18n.t('/QuoteActions/Forms/SalesOrder/SuccessAdditionalOrderHeaderTitle') }}
        </p>

        <p v-if="requestSalesOrderResult.idsStatusMessage">
          {{ requestSalesOrderResult.idsStatusMessage }}
        </p>

        <p v-if="reserveOwnStockCarResponseMessage">
          {{ reserveOwnStockCarResponseMessage }}
        </p>
      </div>
      <div v-else>
        <!-- errors :( -->
        <p>{{ i18n.t('/QuoteActions/Forms/SalesOrder/ErrorText') }}</p>
        <pre v-for="(error, index) in requestSalesOrderResult.quoteErrors" :key="index">
        {{ error }}
        </pre>
        <p>
          {{ i18n.t('/QuoteActions/Forms/SalesOrder/RequestMessageLabel') }}
        </p>
        <pre
          v-for="(error, index) in requestSalesOrderResult.requestingSalesOrderErrors"
          :key="index"
        >
        {{ error }}
        </pre>
        <p v-if="showSendToAutolineErrorMessage">
          {{ i18n.t('/QuoteActions/Forms/SalesOrder/SendToAutolineErrorMessage') }}
        </p>
      </div>

      <div class="buttons">
        <app-button data-qa="close-sales-order-form-button" @click="$emit('close')">
          {{ i18n.t('/Common/Close') }}
        </app-button>
        <app-button
          v-if="showLocalSigningUrlButton"
          data-qa="open-signin-url-button"
          @click="openSigninUrl()"
        >
          {{ i18n.t('/QuoteActions/Forms/DigitalSigning/OpenLocalUrlText') }}
        </app-button>
      </div>
    </div>
  </div>
  <div v-else>
    <loader />
    <div v-if="orderAmountMoreThanOne">
      <p>{{ i18n.t('/QuoteActions/Forms/SalesOrder/ProcessingOrders') }}</p>
      <p>{{ requestedOrdersCount }}/{{ orderAmount }}</p>
    </div>
  </div>
</template>

<script>
import AppFieldset from '@/components/generic/presentational/atoms/appFieldset/AppFieldset.vue'
import FormInput from '@/components/generic/presentational/atoms/formInput/FormInput.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import FormTextArea from '@/components/generic/presentational/atoms/formTextarea/FormTextarea.vue'
import FormCheckbox from '@/components/generic/presentational/atoms/formCheckbox/FormCheckbox.vue'
import DigitalSigningForm from '@/components/generic/presentational/organisms/quoteActions/DigitalSigningForm.vue'
import DigitalLoginRedirectForm from '@/components/generic/presentational/organisms/quoteActions/DigitalLoginRedirectForm.vue'
import DigitalSigning_Api from '@/api/server/DigitalSigning.api.js'
import quoteOrderService from '@/services/quoteOrderService.js'
import stockCarsService from '@/services/stockCarsService'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import StoreConnector from '@/helpers/StoreConnector'
import EmailBodyHelper from '@/helpers/EmailBodyHelper'
import AdditionalOrder from '@/mixins/additionalOrder.js'
import CheckExpectedDeliveryDate from '@/mixins/checkExpectedDeliveryDate'
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'
import { EventBus } from '@/plugins/event-bus'
import ConfirmationPrompt from '@/components/generic/presentational/molecules/confirmationPrompt/ConfirmationPrompt.vue'
import digitalSigningStatus from '@/helpers/enums/digitalSigningStatus'
import entityType from '@/helpers/enums/entityType'

export default {
  components: {
    AppButton,
    AppFieldset,
    FormInput,
    FormTextArea,
    FormCheckbox,
    Loader,
    DigitalSigningForm,
    Modal,
    DigitalLoginRedirectForm,
    ConfirmationPrompt,
  },
  mixins: [AdditionalOrder, CheckExpectedDeliveryDate],
  emits: ['close', 'saveLease', 'signingLocally'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,

      emailBody: '',
      sendEmailToCustomer: false,
      sendCopyToEmployee: true,
      signDigitally: false,
      signManually: false,
      sendEmailOnly: false,
      reserveInIDS: false,
      reserveStockCar: false,
      digitalSigningParams: null,

      requestedSalesOrder: false,
      loading: false,
      showDigitalSigningModal: false,
      showDigitalLoginRedirectModal: false,
      showManualSigningPrompt: false,
      digitalRedirectUrl: null,

      orderMultiple: false,
      orderAmount: 1,
      requestedOrdersCount: 0,

      hasErrors: false,

      requestSalesOrderResult: {},
      reserveOwnStockCarResult: {},

      // local signing parameters
      signingUrl: '',
      signingQrCode: null,
      signLocally: false,
      goPrinterEnabled: false,

      // approval-specific data
      isProcessingApproval: false,
      showApprovalConfirmModal: false,
    }
  },
  computed: {
    ...mapState({
      isSalesOrder(state, getters) {
        return getters[this.quoteNamespace + '/quoteStatusIsSalesOrder']
      },
      currentQuote(state, getters) {
        return getters[this.quoteNamespace + '/get']
      },
      isStockCarOrder(state, getters) {
        return getters[this.quoteNamespace + '/isStockCar']
      },
      quoteCar(state, getters) {
        return getters[this.quoteNamespace + '/car']
      },
      isSigningStatusEmpty(state, getters) {
        return getters[this.quoteNamespace + '/quoteSigningStatusIsEmpty']
      },
    }),
    ...mapState('user', ['dealerId']),
    ...mapGetters({
      dealerSettings: 'user/dealerSettings',
      quoteShowInPdf: 'quote/showVwpfsPrivateLeasePdf',
    }),
    entityType() {
      return entityType
    },
    salesApprovalEnabled() {
      return !!this.dealerSettings?.salesApprovalEnabled
    },
    approvalFormTitle() {
      return this.i18n.t('/QuoteActions/Forms/Approval/RequestSalesOrderApprovalTitle')
    },
    sendForApprovalButtonText() {
      return this.i18n.t('/QuoteActions/Buttons/SendForApproval')
    },
    approvalFormDescription() {
      return this.i18n.t('/QuoteActions/Forms/Approval/Description')
    },
    currentUser() {
      return window.info?.user?.UserInfo?.UserName
    },
    signingQrCodeImageSrc() {
      return `data:image/jpeg;base64,${this.signingQrCode}`
    },
    quoteId() {
      return this.currentQuote.QuoteId
    },
    customerInfo() {
      return this.currentQuote.QuoteDetails.Customer
    },
    mailCustomerLabel() {
      if (this.customerInfo?.CustomerEmail != null)
        return `${this.i18n.t('/QuoteActions/Forms/MailConfirmationToCustomerLabel')}: ${
          this.customerInfo?.CustomerEmail
        }`
      else return this.i18n.t('/QuoteActions/Forms/MailConfirmationToCustomerLabel')
    },
    mailCopyToEmployee() {
      return `${this.i18n.t('/QuoteActions/Forms/MailConfirmationCopyLabel')}: ${
        window.info?.user?.UserInfo?.Email
      }`
    },
    maxOrderAmountError() {
      return this.orderAmount > 25
    },
    orderMultipleErrorMessage() {
      if (this.signDigitallyAndMakeMultipleSelected)
        return this.i18n.t(
          '/quoteactions/forms/salesorder/signDigitallyAndMakeMultipleSelectedError'
        )

      if (this.maxOrderAmountError)
        return this.i18n.t('/quoteactions/forms/salesorder/maximumnumberorderserrormessage')

      return null
    },
    stockCarId() {
      return this.quoteCar.StockCarId
    },
    reserveOwnStockCarButtonEnabled() {
      return (
        this.dealerSettings?.claimOwnStockCarEnabled &&
        this.isStockCarOrder &&
        !this.additionalQuote
      )
    },
    showLocalSigningUrlButton() {
      return !this.additionalQuote && !this.hasErrors && this.signLocally
    },
    showSendToAutolineErrorMessage() {
      return (
        this.hasErrors &&
        !this.signDigitally &&
        !this.signManually &&
        !this.currentQuote.salesOrderSendToAutoline
      )
    },
    digitalSigningEnabled() {
      return this.dealerSettings?.pdfSigningEnabled && !this.additionalQuote
    },
    orderAmountMoreThanOne() {
      return this.orderMultiple && this.orderAmount > 1
    },
    signDigitallyAndMakeMultipleSelected() {
      return this.signDigitally && this.orderAmountMoreThanOne
    },
    reserveOwnStockCarResponseMessage() {
      let responseBody = this.reserveOwnStockCarResult?.Body
      if (!responseBody) return null

      if (!responseBody?.message) {
        const reservationEndDate = moment(responseBody).format('D-M-YYYY')
        const reservationSuccess = this.i18n.t('/QuoteActions/Forms/ReserveOwnStockCarSuccess', {
          date: reservationEndDate,
        })

        return reservationSuccess
      }

      if (responseBody?.dealerId != this.dealerId) return null
      let claimPeriodEndDate = moment.utc(responseBody.claimPeriodEndDate, 'YYYY-MM-DD')
      if (claimPeriodEndDate.isBefore(moment.utc(), 'day')) return null

      let messageTemplate = this.i18n.t('/QuoteActions/Forms/ReserveOwnStockCarAlreadyReserved', {
        date: claimPeriodEndDate.format('DD-MM-YYYY'),
        establishment: responseBody.establishmentId,
        user: responseBody.salesEmail,
      })

      return messageTemplate
    },
    manualSigningTitle() {
      return this.expectedDeliveryDateUnknownAndForced
        ? this.i18n.t('/expected delivery date/error/title')
        : this.i18n.t('/QuoteActions/Forms/ManualSign/Title')
    },
    manualSigningText() {
      return this.expectedDeliveryDateUnknownAndForced
        ? this.i18n.t('/expected delivery date/error/text')
        : this.i18n.t('/QuoteActions/Forms/ManualSign/Text')
    },
  },
  created() {
    // set default emailbody if there isn't one yet
    if (this.currentQuote.QuoteDetails.EmailBodySalesOrder == null)
      this.emailBody = EmailBodyHelper.getRequestSalesorderEmailBody(
        this.currentQuote.QuoteDetails.Customer
      )
    else this.emailBody = this.currentQuote.QuoteDetails.EmailBodySalesOrder
  },
  methods: {
    async submitForApproval() {
      if (!this.quoteId || !this.currentUser) {
        console.error('Missing quoteId or currentUser for approval')
        return
      }

      this.isProcessingApproval = true
      this.loading = true

      try {
        let salesOrderParams = {
          quoteId: this.quoteId,
          sendEmailToCustomer: this.sendEmailToCustomer,
          sendCopyToEmployee: this.sendCopyToEmployee,
          sendSigning: false, // No signing for approval workflow
          digitalSigningParams: null,
          reserveInIds: this.reserveInIDS,
          requestedBy: this.currentUser,
          isApprovalRequest: true,
        }
        await quoteOrderService.requestSalesOrder(salesOrderParams)

        this.requestedSalesOrder = true
      } catch (error) {
        console.error('Approval request failed:', error)
        this.hasErrors = true
        this.requestSalesOrderResult = {
          salesOrderHasErrors: true,
          quoteHasErrors: true,
          quoteErrors: ['Er is een fout opgetreden bij het versturen ter goedkeuring.'],
          requestingSalesOrderErrors: [error.message || 'Onbekende fout'],
        }
      } finally {
        this.isProcessingApproval = false
        this.loading = false
      }
    },
    async manualQuoteSignSubmit() {
      this.signManually = true
      this.showManualSigningPrompt = false
      await this.requestSalesOrder()
    },
    async manualQuoteSignSkip() {
      this.signManually = false
      this.showManualSigningPrompt = false
      await this.requestSalesOrder()
    },
    openSigninUrl() {
      if (!!this.signingUrl) window.open(this.signingUrl)
    },
    toggleDigitalLoginRedirectModal(show) {
      this.showDigitalLoginRedirectModal = show
    },
    async toggleDigitalSigningModal(show) {
      if (show) {
        let authData = await DigitalSigning_Api.CheckAuthentication()
        let redirectUrl = authData?.Body?.AuthenticationDetails?.AuthorizationUrl
        this.goPrinterEnabled = authData?.Body?.GoPrinterEnabled
        if (redirectUrl) {
          this.digitalRedirectUrl = redirectUrl
          this.toggleDigitalLoginRedirectModal(true)
        } else if (!this.goPrinterEnabled) {
          this.showDigitalSigningModal = show
        } else {
          let digitalSigningParams = {
            signLocally: true,
          }
          this.submitDigitalSigning(digitalSigningParams)
        }
      } else {
        this.showDigitalSigningModal = show
      }
    },
    async submit() {
      // If sales approval is enabled, show the second popup instead of processing
      if (this.salesApprovalEnabled && !this.isSalesOrder) {
        this.showApprovalConfirmModal = true
        return
      }

      // Original logic for when approval is disabled or for sales orders
      if (this.isSalesOrder) {
        this.sendEmailOnly = true
        await this.requestSalesOrder()
      }

      if (this.signDigitally) {
        await this.toggleDigitalSigningModal(true)
      } else {
        if (!this.additionalQuote) {
          this.showManualSigningPrompt = true
        } else {
          await this.manualQuoteSignSkip()
        }
      }
    },
    async submitDigitalSigning(digitalSigningParams) {
      this.digitalSigningParams = digitalSigningParams
      await this.toggleDigitalSigningModal(false)
      await this.requestSalesOrder()
    },
    async requestSalesOrder() {
      this.loading = true
      this.reserveOwnStockCarResult = {}

      await this.$store.dispatch(`${this.quoteNamespace}/setEmailBodySalesOrder`, this.emailBody)

      await StoreConnector.dispatch(`${this.quoteNamespace}/update`)

      if (this.signManually) {
        this.$store.commit('quote/setSigningStatus', digitalSigningStatus.manualSignCompleted)

        await quoteOrderService.changeSigningStatus(
          this.quoteId,
          digitalSigningStatus.manualSignCompleted
        )
      }

      let salesOrderParams = {
        quoteId: this.quoteId,
        sendEmailToCustomer: this.sendEmailToCustomer,
        sendCopyToEmployee: this.sendCopyToEmployee,
        digitalSigningParams: this.digitalSigningParams,
        sendSigning: this.signDigitally || this.signManually,
        reserveInIds: this.reserveInIDS,
        isApprovalRequest: false,
      }

      if (this.sendEmailOnly) {
        salesOrderParams.sendSigning = true
      }

      // first updateQuote, then request Salesorder
      this.requestSalesOrderResult = await quoteOrderService.requestSalesOrder(salesOrderParams)

      this.hasErrors =
        this.requestSalesOrderResult.salesOrderHasErrors ||
        this.requestSalesOrderResult.quoteHasErrors

      if (this.quoteShowInPdf && !this.sendEmailOnly) {
        await EventBus.$emit('saveLease')
      }

      if (this.hasErrors) {
        this.requestedSalesOrder = true
        this.loading = false
        return
      }

      if (this.stockCarId && this.reserveStockCar && !this.hasErrors) {
        let params = {
          stockCarId: this.stockCarId,
          quoteId: this.quoteId,
          establishmentId: this.currentQuote.QuoteDetails?.Dealer?.EstablishmentID,
        }
        this.reserveOwnStockCarResult = await stockCarsService.setStockCarReservation(params)
      }

      if (this.orderMultiple) {
        for (let i = 1; i < this.orderAmount; i++) {
          this.requestedOrdersCount++
          let duplicateQuoteResult = await quoteOrderService.duplicateQuote()

          let duplicateQuoteRequestParams = {
            quoteId: duplicateQuoteResult.data.QuoteId,
            sendEmailToCustomer: this.sendEmailToCustomer,
            sendCopyToEmployee: this.sendCopyToEmployee,
            digitalSigningParams: this.digitalSigningParams,
            sendSigning: this.signDigitally,
            reserveInIds: false,
            isApprovalRequest: false,
          }

          // first updateQuote, then request Salesorder
          this.requestSalesOrderResult = await quoteOrderService.requestSalesOrder(
            duplicateQuoteRequestParams
          )
        }
      }
      this.signLocally = this.digitalSigningParams?.signLocally
      if (this.signLocally) {
        this.signingUrl = await DigitalSigning_Api.getSigningUrl({
          entityId: this.quoteId,
          entityType: entityType.quote,
        })

        if (!this.goPrinterEnabled) {
          this.signingQrCode = await DigitalSigning_Api.getSigningQrCode({
            entityId: this.quoteId,
            entityType: entityType.quote,
          })
        }

        this.$emit('signingLocally', this.digitalSigningParams.signLocally)
      }

      this.loading = false
      this.requestedSalesOrder = true
    },
  },
}
</script>

<style lang="scss" scoped>
pre {
  white-space: normal;
}

.signing-qr-code-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.875rem;
  .signing-qr-code {
    background-color: lightcoral;
    width: 200px;
    height: 200px;
  }
}

.buttons {
  display: flex;
  justify-content: space-between;
}

.modal-buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}
</style>
